/**
 * ApprovalsPage — approval queue filtered by project (APR-1). Cards show
 * title, requester (conversation link), requested actions and choices as
 * buttons; Decide approves with a choice or rejects. History tab lists all
 * resolved approvals.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { useGetApprovalsQuery, useDecideApprovalMutation } from '@/store/api/approvalsApi';
import { useGetProjectsQuery } from '@/store/api/projectsApi';
import type { Approval } from '@/types';
import { format, formatDistanceToNow } from 'date-fns';

export function ApprovalsPage() {
  const toast = useToast();
  const [projectFilter, setProjectFilter] = useState('');
  const { data: approvals, isLoading, refetch } = useGetApprovalsQuery(
    projectFilter ? { projectId: projectFilter } : undefined,
  );
  const [decide, { isLoading: isDeciding }] = useDecideApprovalMutation();
  const { data: projects } = useGetProjectsQuery();

  const approvalList = (approvals as Approval[] | undefined) ?? [];
  const projectList = (projects as Array<{ _id: string; name: string }> | undefined) ?? [];
  const projectName = (id: string) => projectList.find((p) => p._id === id)?.name ?? id.slice(0, 8);

  const pending = approvalList.filter((a) => a.status === 'pending');
  const history = approvalList.filter((a) => a.status !== 'pending');

  const doDecide = async (approval: Approval, approve: boolean, choiceId?: string) => {
    try {
      const res = await decide({ id: approval._id, approve, choiceId }).unwrap();
      toast.success(approve ? 'Approved' : 'Rejected');
      if (approve && (res as { token?: string }).token) {
        toast.info(
          `Ephemeral token issued (expires ${(res as { tokenExpiresAt?: string }).tokenExpiresAt ? new Date((res as { tokenExpiresAt: string }).tokenExpiresAt).toLocaleTimeString() : 'soon'})`,
        );
      }
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Decision failed');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Approvals</h1>
          <p className="text-sm text-muted-foreground">
            Queue and history of approval requests
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground shrink-0">Project</Label>
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="">All projects</option>
            {projectList.map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={() => refetch()}>Refresh</Button>
        </div>
      </div>

      <Tabs defaultValue="queue">
        <TabsList>
          <TabsTrigger value="queue" className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Queue ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="history">History ({history.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-3">
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : pending.length === 0 ? (
            <EmptyState title="No pending approvals" description="The queue is clear 🎉" />
          ) : (
            <div className="grid gap-3 lg:grid-cols-2">
              {pending.map((a) => (
                <Card key={a._id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <CardTitle className="text-base">{a.title}</CardTitle>
                        <p className="text-xs text-muted-foreground">
                          Project: {projectName(a.projectId)} · expires {a.expiresAt ? formatDistanceToNow(new Date(a.expiresAt), { addSuffix: true }) : '—'}
                        </p>
                      </div>
                      <Badge variant="secondary" className="shrink-0 text-[10px]">{a.status}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {a.description && <p className="text-sm text-muted-foreground">{a.description}</p>}

                    {a.requester?.conversationId && (
                      <p className="text-xs">
                        Requester:{' '}
                        <Link to={`/conversations/${a.requester.conversationId}`} className="font-medium text-primary hover:underline">
                          conversation {a.requester.conversationId.slice(0, 10)}… <ExternalLink className="inline h-3 w-3" />
                        </Link>
                      </p>
                    )}

                    {a.requestedActions.length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium">Requested actions</p>
                        {a.requestedActions.map((act, i) => (
                          <div key={i} className="flex items-center gap-2 rounded-md border px-2 py-1 text-xs font-mono">
                            <Badge variant="outline" className="text-[10px]">{act.service}</Badge>
                            <span>{act.method}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {a.choices.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {a.choices.map((c) => (
                          <Button
                            key={c.id}
                            size="sm"
                            variant="outline"
                            disabled={isDeciding}
                            onClick={() => doDecide(a, true, c.id)}
                          >
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5 text-green-600" /> {c.label}
                          </Button>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button size="sm" variant="default" disabled={isDeciding} onClick={() => doDecide(a, true)}>
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" disabled={isDeciding} onClick={() => doDecide(a, false)}>
                        <XCircle className="mr-1 h-3.5 w-3.5 text-destructive" /> Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          {history.length === 0 ? (
            <EmptyState title="No history" description="Resolved approvals will appear here" />
          ) : (
            <div className="space-y-2">
              {history.map((a) => (
                <div key={a._id} className="flex items-center justify-between rounded-md border px-3 py-2">
                  <div>
                    <p className="text-sm font-medium">{a.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {projectName(a.projectId)} · decided {a.decidedAt ? format(new Date(a.decidedAt), 'MMM d, yyyy HH:mm') : '—'}
                      {a.decidedBy ? ` by ${a.decidedBy}` : ''}
                      {a.decisionChoiceId ? ` · choice ${a.decisionChoiceId}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        a.status === 'approved' ? 'default' : a.status === 'rejected' ? 'destructive' : 'secondary'
                      }
                      className="text-xs"
                    >
                      {a.status}
                    </Badge>
                    {a.requester?.conversationId && (
                      <Link to={`/conversations/${a.requester.conversationId}`} className="text-xs text-muted-foreground hover:text-primary">
                        context <ExternalLink className="inline h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}